import { FileExportArray } from '../../../data/data-interfaces.internal.ts';
import { ExportBuilder, dispatchExportStatus } from '../exportBuilder.ts';
import { GrabCADExport, ExportOptions } from './GrabCADTypes.ts';
import {
  validateForGrabCAD, buildGrabCADExport, grabCadTX, GrabCadOptions,
  grabCadImportFile, grabCadConvertFiles, grabCadDroppedFilePath, grabCadSaveFile,
  type GrabCadImportOptions, type GrabCadStlUnit,
} from './grabcad.ts';
import { registerEnvironmentEnv, registerOrgIdEnv } from '../../../data/envRegistry.ts';
import { registerAppSettings } from '../../../data/appSettings.ts';
import { registerFileImportProvider, type FileImportContext } from '../../../components/file/fileImportProvider.ts';
import { registerFileSaveProvider } from '../../fileSave.ts';
import './as-grabcad-export-button.ts';
import './as-modal-grabcad-success.ts';


declare module '../exportBuilder.ts' {
  interface ExportBuilder {
    toGrabCAD(opts?: ExportOptions): Promise<GrabCADExport>;
    sendToGrabCAD(opts?: ExportOptions & GrabCadOptions): Promise<string>;
  }
}

/**
 * Plugin for ExportBuilder that adds GrabCAD export and transmission capabilities.
 */
export function grabcadPlugin(builder: ExportBuilder) {
  return {
    /**
     * Build and export to GrabCAD format.
     * For each auto-fetch entry: runs beforeExport() then fetches data.
     * All fetches run sequentially. Validates all mesh parts have fileType '3mf' before assembling.
     */
    async toGrabCAD(opts: ExportOptions = {}): Promise<GrabCADExport> {
      const resolvedFiles = await builder.resolve({ meshFileType: '3mf' });

      const fileArray = resolvedFiles as FileExportArray;
      const validation = validateForGrabCAD(fileArray);
      if (!validation.valid) {
        const lines = validation.errors.map((e) => `  - "${e.fileName ?? e.fileKey}": ${e.message}`);
        throw new Error(`[ExportBuilder] GrabCAD validation failed:\n${lines.join('\n')}`);
      }

      return buildGrabCADExport(fileArray, opts);
    },

    /**
     * Convenience wrapper: resolves to GrabCAD format then transmits in one call.
     * Returns the assembly ID from GrabCAD.
     */
    async sendToGrabCAD(opts: ExportOptions & GrabCadOptions = {}): Promise<string> {
      const id = builder.exporterId;
      dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'pending' });
      try {
        const { target, targetOrigin, listenOn, ...exportOpts } = opts;
        const pkg = await this.toGrabCAD(exportOpts);
        if (pkg.parts.length === 0) {
          document.querySelector('as-app')?.dispatchEvent(new CustomEvent('as-notify', {
            detail: { defaultText: 'No parts to export. Check that your export scope is configured correctly.', type: 'error', persist: true },
          }));
          throw new Error('No mesh parts to export');
        }
        const assemblyId = await grabCadTX(pkg, { target, targetOrigin, listenOn });
        dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'success', message: `id: ${assemblyId}` });
        // Unified data-export log: a GrabCAD transfer is a data_export with
        // subcategory 'grabcad' (see as-app's as-data-export listener). No separate
        // started event — we only log on a completed (or failed) send.
        document.querySelector('as-app')?.dispatchEvent(new CustomEvent('as-data-export', {
          bubbles: true, composed: true,
          detail: { subcategory: 'grabcad', assemblyId, fileInfo: pkg.parts.map((p) => ({ sizeBytes: p.data.byteLength, fileType: p.extension })) },
        }));
        return assemblyId;
      } catch (err: any) {
        dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'error', message: err.message });
        // Fold the failure into the same data_export log via detail.error.
        document.querySelector('as-app')?.dispatchEvent(new CustomEvent('as-data-export', {
          bubbles: true, composed: true, detail: { subcategory: 'grabcad', error: err.message },
        }));
        throw err;
      }
    }
  };
}

ExportBuilder.registerPlugin(grabcadPlugin);

ExportBuilder.registerButton({
  tagName: 'as-grabcad-export-button',
  requiredEnv: 'grabcad',
  panelPrefix: 'grabcad',
  extraAttrs: ['package-name'],
  defaultAttrs: {
    'default-text': 'Transfer to GrabCAD Print',
    'tooltip-default-text': 'Transfer your design to GrabCAD Print. Models will be placed on the build tray, where you can prepare them for printing. Note: Depending on your selection, you can switch between the assembly and individual parts in the GrabCAD Print model tree.',
  },
});

ExportBuilder.registerModal({ tagName: 'as-modal-grabcad-success', id: 'grabcad-success', disableBgClose: true });

function importOpts(ctx: FileImportContext): GrabCadImportOptions {
  return {
    unit: (ctx.getAttribute('grabcad-stl-unit') ?? 'mm') as GrabCadStlUnit,
    multifile: ctx.getAttribute('multiple') !== null,
    onProgress: ctx.onProgress,
  };
}

// File import provider for <as-file-upload>: GrabCAD's native open/convert
// flow replaces the standard file input.
//   Browse: open-file dialog + convert (grabCadImportFile; the dialog allows
//           multi-select when the component has the `multiple` attribute).
//   Drop:   the OS path arrives on the dropped File, so the dialog step is
//           skipped and each path goes straight to convert (sequentially —
//           the protocol correlates one conversion at a time). Blobs without
//           an OS path (internal transfers, synthetic blobs) are declined and
//           take the normal drop flow.
// kinds: ['mesh'] — GrabCAD's open dialog and convert pipeline only handle
// CAD/mesh files. Image/text/data uploads (category != 'mesh') bypass this
// provider and use the browser's native file input instead.
registerFileImportProvider('grabcad', {
  kinds: ['mesh'],
  pickFile: (ctx) => grabCadImportFile(importOpts(ctx)),
  claimDrop: (blobs, ctx) => {
    const paths = blobs.map(grabCadDroppedFilePath).filter((p): p is string => !!p);
    return paths.length ? () => grabCadConvertFiles(paths, importOpts(ctx)) : null;
  },
});

// File save provider: saveFileAs() routes here in the GrabCAD environment,
// replacing the browser <a download> with GrabCAD's native save dialog.
// FileSaveOptions (title/fileExtension/fileExtensionName) map 1:1 onto the
// saveFile message fields.
registerFileSaveProvider('grabcad', {
  saveStatusTrackable: true,
  saveFile: (blob, filename, opts) => grabCadSaveFile(blob, filename, opts),
});

registerOrgIdEnv(-1, 'grabcad');
registerEnvironmentEnv('grabcad', 'grabcad');

// GrabCAD Print opens the destination app in its own embedded webview; a browser
// "open in new tab" makes no sense there, so suppress that transfer-modal option.
registerAppSettings('grabcad', { allowTransferNewTab: false });
